import React, { Fragment, useCallback, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { mergeDeepRight, not, remove } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import ReadOnlyField from "../../components/ReadOnlyField";
import YesNoCard from "../../components/YesNoCard";
import CustomDropdownDialog from "../edit/CustomDropdownDialog";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

/**
 * Shared card component for Sub Projects and Custom Dropdown field options
 *
 * | STIP name          | Manager Panel UI name |
 * | ------------------ | --------------------- |
 * | "Sub Projects"     | "Custom Dropdown 1"   |
 * | "Custom Dropdown"  | "Custom Dropdown 2"   |
 *
 * @param {Object} fieldNames - field names for dropdown options in edit dialog
 * @param {'subProjectsLabel' | 'customDropdownLabel'}
 * @param {'subProjectsRequired' | 'customDropdownRequired'}
 * @param {'sub_projects' | 'custom_dropdown'} fieldOptionProp - name of the field option prop used by libraries-share-ui
 * @param {'subProjects' | 'customDropdownItems'} dropdownItemsProp - name of the field on the project that stores the options for each custom dropdown
 * @param {'subProjectName' | 'customDropdownName'} dropdownItemNameProp - the 'name' of the dropdown option. See table columns for stip.sub_project and stip.custom_dropdown
 * @param {'subProjectId' | 'customDropdownId'} dropdownItemIdProp - the 'id' of the dropdown option. See table columns for stip.sub_project and stip.custom_dropdown
 */
const CustomDropdownCard = ({
  title,
  fieldNames,
  fieldOptionProp,
  dropdownItemsProp,
  dropdownItemNameProp,
  dropdownItemIdProp,
  project,
  loading,
  onUpdate,
}) => {
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setDialogOpen(false);
      console.log(data);
      const update = mergeDeepRight(project, {
        fieldOptions: {
          [fieldOptionProp]: {
            fieldLabel: data[fieldNames.label],
            required: data[fieldNames.required],
          },
        },
        [dropdownItemsProp]: data[dropdownItemsProp],
      });
      console.log(update);
      onUpdate(update);
    },
    [fieldNames, project, onUpdate, dropdownItemsProp, fieldOptionProp],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const update = mergeDeepRight(project, {
        [dropdownItemsProp]: remove(idx, 1, project[dropdownItemsProp]),
      });

      console.log(update);
      onUpdate(update);
    },
    [dropdownItemsProp, project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          color: "primary",
          variant: "h6",
        }}
        title={title}
        action={
          <Tooltip title="Edit">
            <IconButton onClick={handleAddClick} size="large">
              <EditIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            {`Error Loading ${title}`}
          </Box>
        )}

        {notBlank(project) && (
          <Grid container spacing={3}>
            <Grid item sm={9}>
              <LabeledReadOnly label="Dropdown Label">
                <ReadOnlyField>
                  {project.fieldOptions[fieldOptionProp]
                    ? project.fieldOptions[fieldOptionProp].fieldLabel
                    : null}
                </ReadOnlyField>
              </LabeledReadOnly>
            </Grid>

            <Grid item sm={3}>
              <LabeledReadOnly label="Required">
                <YesNoCard
                  value={
                    project.fieldOptions[fieldOptionProp] &&
                    project.fieldOptions[fieldOptionProp].required
                  }
                />
              </LabeledReadOnly>
            </Grid>

            <Grid item sm={12}>
              <LabeledReadOnly label="Options">
                <Fragment>
                  {isBlank(project[dropdownItemsProp]) && (
                    <Box padding={5} fontSize="1.4rem" fontStyle="italic">
                      No Custom Dropdown items are set up for this Project.
                    </Box>
                  )}
                  {notBlank(project[dropdownItemsProp]) && (
                    <List dense style={{ maxHeight: 150, overflowY: "auto" }}>
                      {mapIndexed(
                        (cd, idx) => (
                          <ListItem
                            key={
                              cd[dropdownItemIdProp] || cd[dropdownItemNameProp]
                            }
                          >
                            <ListItemText
                              primary={cd[dropdownItemNameProp]}
                              secondary={not(cd.active) ? "INACTIVE" : null}
                              secondaryTypographyProps={{
                                color: "error",
                                variant: "subtitle2",
                              }}
                            />
                            <ListItemSecondaryAction>
                              {isBlank(cd.customDropdownId) && (
                                <ConfirmButton
                                  handleDelete={() => handleDeleteClick(idx)}
                                  dialogTitle="Remove Custom Dropdown"
                                  dialogBody="Are you sure you want to remove this custom dropdown?"
                                />
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        ),
                        project[dropdownItemsProp],
                      )}
                    </List>
                  )}
                </Fragment>
              </LabeledReadOnly>
            </Grid>
          </Grid>
        )}
      </CardContent>
      {dialogOpen && (
        <CustomDropdownDialog
          open={dialogOpen}
          project={project}
          fieldOptions={project.fieldOptions}
          onCancel={handleCancel}
          onApply={handleDialogApply}
          fieldOptionProp={fieldOptionProp}
          dropdownItemsProp={dropdownItemsProp}
          dropdownItemNameProp={dropdownItemNameProp}
          dropdownItemIdProp={dropdownItemIdProp}
          fieldNames={fieldNames}
        />
      )}
    </Card>
  );
};

export default CustomDropdownCard;
